import { ExpandMore, GTranslate } from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "components/NoScrollDialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import LoadingButton from "components/LoadingButton";
import MDInput from "components/MDInput";
import { useLanguage } from "contexts/language.context";
import * as React from "react";
import languagesService from "services/languages-service";

export default function TranslationsEditNewDialog({
  isOpen,
  onClose,
  onSubmit,
  data,
  type,
  languages,
}) {
  const { label } = useLanguage();
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [translation, setTranslation] = React.useState({
    description: "",
  });
  const [menuOpen, setMenuOpen] = React.useState(null);

  React.useEffect(() => {
    if (!isOpen) return;
    setOpen(true);
    setLoading(false);
    setTranslation({
      description: "",
    });
  }, [isOpen]);

  React.useEffect(() => {
    setTranslation({ ...data });
  }, [data]);

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  async function translateFromTo(from, to) {
    setMenuOpen({ ...menuOpen, loading: to });
    const [response, data] = await languagesService.translate({
      content: translation[from].content,
      to,
      from,
    });

    if (response.ok) {
      setTranslation({
        ...translation,
        [to]: {
          ...(translation[to] || {}),
          content: data,
        },
      });
    }

    setMenuOpen(false);
  }

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: "form",
        onSubmit: async (event) => {
          event.preventDefault();

          setLoading(true);
          const result = await onSubmit(translation);
          if (result.success) {
            handleClose();
          }

          setLoading(false);
        },
      }}
    >
      <DialogTitle>
        {type == "edit"
          ? label("edit-translation")
          : label("create-translation")}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1.5 }}>
          {languages?.map((language) => {
            return (
              <Grid
                item
                xs={12}
                key={"Lang: " + language.id}
                sx={{
                  position: "relative",
                  boxSizing: "border-box",
                }}
              >
                <MDInput
                  autoFocus
                  required
                  margin="dense"
                  id={"" + language.id}
                  name={"" + language.id}
                  label={language.name}
                  type="text"
                  value={translation["" + language.id]?.content || ""}
                  onChange={(e) => {
                    setTranslation({
                      ...translation,
                      ["" + language.id]: {
                        ...(translation["" + language.id] || {}),
                        content: e.target.value,
                      },
                    });
                  }}
                  fullWidth
                />
                <Box
                  sx={{
                    alignSelf: "end",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "end",
                    gap: 0.5,
                    position: "absolute",
                    top: 5,
                    right: 0,
                    mr: 1,
                    backgroundColor: "white.main",
                    px: 0.5,
                  }}
                >
                  <Tooltip title="Google translate">
                    <Chip
                      icon={
                        menuOpen?.loading == language.id ? (
                          <CircularProgress size={"12px"} />
                        ) : (
                          <GTranslate />
                        )
                      }
                      disabled={menuOpen?.loading == language.id}
                      label={
                        menuOpen?.loading == language.id ? (
                          label("generating")
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                            }}
                          >
                            {label("generate")} <ExpandMore />
                          </Box>
                        )
                      }
                      size="small"
                      clickable
                      onClick={(e) => {
                        setMenuOpen({
                          ...menuOpen,
                          anchor: e.currentTarget,
                          content: languages
                            ?.filter((lang) => lang.id != language.id)
                            .map((fromLanguage) => {
                              return (
                                <MenuItem
                                  sx={{ mx: -1 }}
                                  key={"Lanuga: " + fromLanguage.id}
                                  onClick={() => {
                                    translateFromTo(
                                      fromLanguage.id,
                                      language.id
                                    );
                                  }}
                                >
                                  {fromLanguage.name} -{" "}
                                  {fromLanguage.id.toUpperCase()}
                                </MenuItem>
                              );
                            }),
                        });
                      }}
                    />
                  </Tooltip>
                </Box>
              </Grid>
            );
          })}{" "}
          <Menu
            open={!!menuOpen?.anchor}
            anchorEl={menuOpen?.anchor}
            onClose={() => {
              setMenuOpen({ ...menuOpen, anchor: undefined });
            }}
            slotProps={{
              paper: {
                sx: {
                  width: "200px",
                },
              },
            }}
          >
            <Typography
              sx={{ fontSize: "1rem", fontWeight: "500", px: 1, pb: 1 }}
            >
              {label("source-language")}
            </Typography>
            {menuOpen?.content}
          </Menu>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{label("cancel-button")}</Button>
        <LoadingButton
          loading={loading}
          type="submit"
          variant="gradient"
          color="info"
        >
          {type == "edit" ? label("save-button") : label("create-button")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
